// E2E manager: the single entry point the webapp/SDK hook talks to. Owns the
// crypto identity, prekeys and per-peer Double-Ratchet sessions, and turns a
// plaintext byte buffer into a wire envelope (and back).
//
// `store` is a tiny async KV interface: get(key)->value|undefined, set(key,value),
// del(key). In the browser it is backed by IndexedDB (structured clone stores the
// Uint8Arrays in session state directly — no manual serialization); tests use an
// in-memory Map. Private keys never leave it (КР-8, ТП-3).

import { loadAead } from './aead.js';
import { random, generateDHKeyPair, sign } from './asym.js';
import { generateIdentity, generatePrekeys, publicBundle } from './x3dh.js';
import { startSession, acceptSession, encrypt as sesEncrypt, decrypt as sesDecrypt } from './session.js';
import { encodeEnvelope, decodeEnvelope, encodeBundle,
  encodeGroupEnvelope, decodeGroupEnvelope } from './wire.js';

// Byte compare for identity keys. Returns false if either side is missing, so an
// untagged (pre-upgrade) session counts as "not matching" and gets re-handshaked.
// Wall clock in ms. Isolated so it's obvious this is the only time source (used
// for signed-prekey age-based rotation); available in both browser and node.
const nowMs = () => Date.now();

const bytesEqual = (a, b) => {
  if (!a || !b || a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
};

export async function createE2E(store, wasmFactory) {
  const aead = await loadAead(wasmFactory); // gates on oz_selftest (КР-12)

  let identity = await store.get('identity');
  let prekeys = await store.get('prekeys');
  // True when we minted a brand-new identity this run (first ever, or after a device
  // reset). The app uses it to force a full server prekey-pool refresh, which clears
  // any stale pool the previous identity left behind (its private keys are gone).
  let freshIdentity = false;
  if (!identity) {
    identity = generateIdentity();
    prekeys = generatePrekeys(identity);
    await store.set('identity', identity);
    await store.set('prekeys', prekeys);
    freshIdentity = true;
  }

  // One-time-prekey rotation state (Phase 2 / item 2). The pool `prekeys.opks`
  // holds the CURRENTLY advertised opk plus recently-retired ones we keep so an
  // initiator who already fetched an older bundle can still complete DH4 (the
  // transport, me.public, is a single static blob with no atomic per-fetch
  // dispensing, so a used opk can't be deleted the instant it's consumed without
  // desyncing concurrent initiators). `currentOpkId` is the advertised one;
  // `opkNextId` mints fresh, never-reused ids. Migrate a legacy pool (ids 0..N,
  // no metadata) in place: advertise #0, mint after the max existing id.
  if (prekeys.currentOpkId == null) {
    const maxId = prekeys.opks.reduce((m, o) => Math.max(m, o.id), -1);
    prekeys.opkNextId = maxId + 1;
    prekeys.currentOpkId = prekeys.opks.length ? prekeys.opks[0].id : null;
    // Signed-prekey rotation state: treat the existing spk as freshly created so an
    // upgrade doesn't rotate it immediately (avoids surprise churn); it ages out on
    // the normal schedule. `retiredSpks` keeps recently-replaced signed prekeys so
    // an initiator who fetched an older bundle can still complete DH1/DH3.
    prekeys.spkCreated = nowMs();
    prekeys.retiredSpks = [];
    await store.set('prekeys', prekeys);
  }

  // How many retired one-time prekeys to retain before pruning (deleting) the
  // oldest — pruning is what actually delivers forward secrecy for long-past
  // first-contacts (the private key is gone, so a future device compromise can't
  // recompute those sessions' initial DH4). Sized generously so a peer holding a
  // stale cached bundle keeps working in practice.
  const MAX_RETIRED_OPKS = 40;

  // Signed-prekey rotation: replace the medium-term signed prekey on a schedule so
  // a future compromise of the device's stored keys can't recompute the initial
  // secret of sessions older than the retention window (DH1/DH3 use the signed
  // prekey's private key). `MAX_RETIRED_SPKS` retired signed prekeys are kept so an
  // initiator holding a slightly-stale bundle still agrees; older ones are dropped.
  const SPK_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
  const MAX_RETIRED_SPKS = 3;

  // App-registered callback to republish the (changed) public bundle in me.public
  // after a rotation. Fire-and-forget; never blocks the crypto path.
  let bundlePublisher = null;

  // Consume the advertised one-time prekey and advertise a fresh one. Called only
  // after a handshake that used `consumedId` decrypted successfully. Retains the
  // consumed opk in the pool (so late/concurrent initiators still resolve it) and
  // prunes the oldest beyond the retention window.
  const rotateOpk = (consumedId) => {
    if (consumedId == null || consumedId !== prekeys.currentOpkId) {
      return; // a retired opk was used, or none — already replaced, nothing to do
    }
    const fresh = { id: prekeys.opkNextId++, ...generateDHKeyPair() };
    prekeys.opks.push(fresh);
    prekeys.currentOpkId = fresh.id;
    if (prekeys.opks.length > MAX_RETIRED_OPKS + 1) {
      prekeys.opks.sort((a, b) => a.id - b.id); // oldest first; current (max id) kept
      prekeys.opks = prekeys.opks.slice(prekeys.opks.length - (MAX_RETIRED_OPKS + 1));
    }
    store.set('prekeys', prekeys).catch(() => {});
    if (bundlePublisher) {
      Promise.resolve().then(() => { try { bundlePublisher(); } catch (e) { /* ignore */ } });
    }
  };

  // Consume a SERVER-DISPENSED one-time prekey: the peer fetched a UNIQUE opk from the
  // server pool (handed out exactly once), so once its handshake decrypts we DELETE our
  // private half — true one-time forward secrecy. Distinct from the static currentOpkId,
  // which rotateOpk RETAINS (old clients may still read it from me.public.oz.opk).
  const consumeServerOpk = (usedId) => {
    if (usedId == null) {
      return;
    }
    const before = prekeys.opks.length;
    prekeys.opks = prekeys.opks.filter((o) => o.id !== usedId);
    if (prekeys.opks.length !== before) {
      store.set('prekeys', prekeys).catch(() => {});
    }
  };

  // Generate `n` fresh one-time prekeys, add them to the local pool (private halves kept
  // on-device, КР-8) and return their PUBLIC parts [{id,pub}] for upload to the server
  // pool ({set me opk}). Monotonic ids never collide with existing or retired keys.
  const generateOpkBatch = (n) => {
    const batch = [];
    for (let i = 0; i < n; i++) {
      const opk = { id: prekeys.opkNextId++, ...generateDHKeyPair() };
      prekeys.opks.push(opk);
      batch.push({ id: opk.id, pub: opk.pub });
    }
    if (batch.length) {
      store.set('prekeys', prekeys).catch(() => {});
    }
    return batch;
  };

  // Rotate the signed prekey when it ages past SPK_MAX_AGE_MS (or when `force`).
  // Retires the current one (kept for retry on decrypt) and publishes a freshly
  // signed one. Returns true if it rotated. The app calls this on startup; `force`
  // also backs a future manual "rotate my keys now" action.
  const rotateSignedPrekey = (force) => {
    if (!force && prekeys.spkCreated && (nowMs() - prekeys.spkCreated) < SPK_MAX_AGE_MS) {
      return false;
    }
    prekeys.retiredSpks = prekeys.retiredSpks || [];
    prekeys.retiredSpks.push(prekeys.spk); // keep the private key for late initiators
    if (prekeys.retiredSpks.length > MAX_RETIRED_SPKS) {
      prekeys.retiredSpks = prekeys.retiredSpks.slice(-MAX_RETIRED_SPKS); // drop oldest
    }
    const nspk = generateDHKeyPair();
    prekeys.spk = nspk;
    prekeys.spkSig = sign(identity.sign.priv, nspk.pub); // re-sign under our identity (КР-9)
    prekeys.spkCreated = nowMs();
    store.set('prekeys', prekeys).catch(() => {});
    if (bundlePublisher) {
      Promise.resolve().then(() => { try { bundlePublisher(); } catch (e) { /* ignore */ } });
    }
    return true;
  };

  // Two Double-Ratchet sessions per peer: `send` (we are the X3DH initiator /
  // Alice) and `recv` (we accepted their handshake / Bob). BOTH sides always
  // encrypt with their own `send` and decrypt with the other's `recv`, so a
  // conversation is symmetric and never depends on who "started" it — no plaintext
  // fallback, no single-initiator deadlock, no simultaneous-init desync.
  // Preloaded so encrypt/decrypt stay synchronous in the send/receive path;
  // ratchet state is persisted back in the background (fire-and-forget).
  const cache = {}; // peerUid -> { send, recv }
  for (const { key, value } of await store.list('session:')) {
    const uid = key.slice('session:'.length);
    const entry = (value && (value.send !== undefined || value.recv !== undefined))
      ? value
      : { send: null, recv: null }; // ignore any legacy single-session blob
    // Drop the OUTBOUND session on load: the next message will re-handshake and
    // reliably (re)deliver the init. This heals any session left half-established
    // by an earlier bug/version and costs only one fresh handshake per chat. The
    // INBOUND session is kept so already-received messages still decrypt.
    entry.send = null;
    entry.pendingInit = null;
    cache[uid] = entry;
  }
  const entryFor = (uid) => cache[uid] || (cache[uid] = { send: null, recv: null });
  const persist = (uid) => { store.set('session:' + uid, cache[uid]).catch(() => {}); };

  // Locally-decrypted plaintext, keyed by "topic:seq". The Double Ratchet is not
  // idempotent (a message key is consumed on first decrypt) and the sender cannot
  // decrypt its own ciphertext at all — so once a message's plaintext is known we
  // keep it on-device (as Signal/WhatsApp keep history in a local store) to render
  // it again after a reload and to guarantee each ciphertext is decrypted at most
  // once. Preloaded synchronously so the render path stays synchronous.
  const plainCache = {}; // "topic:seq" -> decrypted content
  for (const { key, value } of await store.list('plain:')) {
    plainCache[key.slice('plain:'.length)] = value;
  }

  return {
    // Public bundle to publish in me.public (ФТ-2), wire-encoded. Reflects the
    // current (rotating) one-time prekey.
    publicBundle: () => encodeBundle(publicBundle(identity, prekeys)),

    // Register the callback that republishes the public bundle after a prekey
    // rotation (one-time or signed) — the app wires this to a me.public setMeta.
    setBundlePublisher: (fn) => { bundlePublisher = fn; },

    // Rotate the signed prekey if it has aged out (called by the app on startup).
    // `force` rotates unconditionally. Returns true if it rotated.
    maybeRotateSignedPrekey: (force) => rotateSignedPrekey(force),

    // Server-side one-time prekey pool (X3DH dispensing). generateOpkBatch(n) mints n
    // fresh prekeys and returns their public parts [{id,pub}] to upload via {set me
    // opk}; opkCount() is how many prekey privates we currently hold on-device.
    generateOpkBatch: (n) => generateOpkBatch(n),
    opkCount: () => prekeys.opks.length,
    // True if the identity was freshly minted this run (forces a full pool refresh).
    identityFresh: () => freshIdentity,

    // Our own identity public keys, for the safety-number fingerprint (ФТ-7).
    myIdentity: () => ({ idDH: identity.dh.pub, idSign: identity.sign.pub }),

    hasSession: (uid) => !!(cache[uid] && (cache[uid].send || cache[uid].recv)),

    // Our OUTBOUND session is valid for the peer's CURRENT identity. When it is
    // not (peer republished a new key, or none yet), the next encrypt rebuilds it
    // and emits a fresh handshake.
    hasValidSession: (uid, peerBundle) => {
      const e = cache[uid];
      return !!(e && e.send && peerBundle && bytesEqual(e.send.peerIdDH, peerBundle.idDH));
    },

    // On-device plaintext history. getPlain returns the cached content for a
    // (topic, seq) or undefined; putPlain stores it once (idempotent — a later
    // re-render or reload must not overwrite/re-decrypt).
    getPlain: (topic, seq) => plainCache[topic + ':' + seq],
    putPlain: (topic, seq, content) => {
      const k = topic + ':' + seq;
      if (plainCache[k] !== undefined) {
        return;
      }
      plainCache[k] = content;
      store.set('plain:' + k, content).catch(() => {});
    },

    // Wipe the on-device DECRYPTED message cache (audit 2nd tier: private-cache cleanup
    // on logout). Called from logout so that clearing the SDK message DB actually removes
    // readable history from this device — identity, prekeys and ratchet sessions are kept,
    // so logging back in does not churn the identity (peers don't have to re-verify).
    wipePlaintext: async () => {
      for (const k of Object.keys(plainCache)) {
        delete plainCache[k];
      }
      try {
        for (const { key } of await store.list('plain:')) {
          await store.del(key);
        }
      } catch (e) { /* best-effort */ }
    },

    // Encrypt plaintext (Uint8Array) for peer `uid` (synchronous). `peerBundle`
    // (decoded) is needed only for the first message (no session yet). `aad`
    // binds topic+sender (КР-7). Returns the wire envelope string.
    // `isMessage` marks a real chat message (vs a call-key exchange). The X3DH
    // handshake is kept "pending" until a MESSAGE has carried it, so handshake
    // delivery is reliable even when the session was first created for a call
    // (whose signaling can be dropped) — the next message re-delivers it.
    encrypt(uid, peerBundle, plaintext, aad, isMessage = false) {
      const entry = entryFor(uid);
      let send = entry.send;
      // Peer published a different identity than our outbound session was built
      // for (new device / reset) → rebuild it so they can decrypt again.
      if (send && peerBundle && !bytesEqual(send.peerIdDH, peerBundle.idDH)) {
        send = null;
        entry.pendingInit = null;
      }
      if (!send) {
        if (!peerBundle) throw new Error('E2E: no session and no peer bundle');
        const started = startSession(identity, peerBundle);
        send = started.session;
        send.peerIdDH = peerBundle.idDH; // bind to the peer's identity key
        entry.send = send;
        entry.pendingInit = started.init;
      }
      const init = entry.pendingInit || null; // (re)deliver the handshake if pending
      const w = sesEncrypt(send, plaintext, aad, aead);
      if (isMessage && entry.pendingInit) {
        entry.pendingInit = null; // a message reliably delivered it — stop resending
      }
      persist(uid);
      return encodeEnvelope({ init, header: w.header, ct: w.ct, tag: w.tag });
    },

    // Drop our outbound session so the next encrypt re-handshakes from a fresh
    // bundle. Used by the auto-recovery when the peer can't be decrypted (they
    // changed identity — see the message-view recovery).
    resetSend: (uid) => {
      const e = cache[uid];
      if (e) { e.send = null; e.pendingInit = null; persist(uid); }
    },

    // Decrypt an envelope from peer `uid` (synchronous). Returns plaintext
    // Uint8Array, or null if it can't be decrypted/authenticated (caller shows a
    // placeholder — ФТ-5).
    decrypt(uid, envelopeStr, aad) {
      let env;
      try { env = decodeEnvelope(envelopeStr); } catch { return null; }
      const entry = entryFor(uid);

      // Try the existing inbound session first (decrypt on a clone so a failed
      // attempt can't corrupt live ratchet state).
      if (entry.recv) {
        const clone = structuredClone(entry.recv);
        const pt = sesDecrypt(clone, env.header, env.ct, env.tag, aad, aead);
        if (pt) { entry.recv = clone; persist(uid); return pt; }
      }

      // The message carries a fresh X3DH handshake — (re)build our inbound session
      // from it and retry. Handles first contact and the peer re-handshaking after
      // an identity change.
      if (env.init) {
        // Replay/rollback guard: if our inbound session was already built from THIS
        // exact handshake (same ephemeral key), a message the live session couldn't
        // decrypt is a REPLAY of an old message — do NOT rebuild/reset the ratchet
        // from it. A genuine re-handshake carries a NEW ephemeral key.
        if (entry.recv && entry.recv.acceptedEk && bytesEqual(entry.recv.acceptedEk, env.init.ek)) {
          return null;
        }
        // The init used ONE of our signed prekeys (current or a recently-retired
        // one — the wire carries no spk id). Try each candidate; the AEAD tag on the
        // first message authenticates the single correct one. Each attempt builds a
        // fresh session, so a wrong guess can't corrupt state.
        const spkCandidates = [prekeys.spk, ...(prekeys.retiredSpks || [])];
        for (const spk of spkCandidates) {
          const ns = acceptSession(identity, { spk, opks: prekeys.opks }, env.init);
          ns.peerIdDH = env.init.idDH; // bind to the initiator's identity key
          ns.acceptedEk = env.init.ek; // remember which handshake this session came from
          const pt = sesDecrypt(ns, env.header, env.ct, env.tag, aad, aead);
          if (pt) {
            entry.recv = ns; persist(uid);
            // First contact succeeded — consume the one-time prekey it used. The
            // replay guard above already dropped replays, so a key never consumes
            // twice. If it was our STATIC advertised opk (me.public.oz.opk), rotate it
            // (retain for old clients). If it was a SERVER-DISPENSED pool key, delete
            // our private half outright (the server handed it out exactly once → true
            // one-time forward secrecy).
            if (env.init.opkId === prekeys.currentOpkId) {
              rotateOpk(env.init.opkId);
            } else {
              consumeServerOpk(env.init.opkId);
            }
            return pt;
          }
        }
      }
      return null;
    },

    // Encrypt one plaintext for a whole group (ФТ-9). `members` is
    // [{uid, bundle}] EXCLUDING self. The body is encrypted once with a fresh
    // random content key K; K is then wrapped for each member over that member's
    // pairwise ratchet (this reuses `encrypt` above, so a first-contact member's
    // wrap carries the X3DH handshake automatically). `wrapAad(uid)` binds each
    // wrap (pairwise sender->member); `contentAad` binds the body (topic+sender).
    // Returns the group envelope string, or null if not one member could be
    // wrapped for (e.g. nobody published a bundle).
    encryptGroup(members, plaintext, wrapAad, contentAad) {
      const key = random(32);
      const iv = random(12);
      const { ct, tag } = aead.encrypt(key, iv, contentAad, plaintext);
      const keys = {};
      let wrapped = 0;
      for (const m of members || []) {
        if (!m || !m.uid || !m.bundle) {
          continue;
        }
        try {
          keys[m.uid] = this.encrypt(m.uid, m.bundle, key, wrapAad(m.uid), true);
          wrapped++;
        } catch (e) {
          // Skip a member we cannot build a session for; the rest still get it.
        }
      }
      if (wrapped === 0) {
        return null;
      }
      return encodeGroupEnvelope({ iv, ct, tag, keys });
    },

    // Decrypt a group envelope addressed to us. `senderUid` is the message author,
    // `myUid` selects our wrapped key, `wrapAad`/`contentAad` mirror the sender's.
    // Returns the plaintext Uint8Array, or null (not addressed to us / can't
    // unwrap / body auth fails).
    decryptGroup(senderUid, envelopeStr, myUid, wrapAad, contentAad) {
      const env = decodeGroupEnvelope(envelopeStr);
      if (!env) {
        return null;
      }
      const mine = env.keys[myUid];
      if (!mine) {
        return null; // we are not (or no longer) a recipient of this message
      }
      const key = this.decrypt(senderUid, mine, wrapAad);
      if (!key) {
        return null; // couldn't unwrap the content key (stale/again-handshake)
      }
      return aead.decrypt(key, env.iv, contentAad, env.ct, env.tag);
    },
  };
}
